import { useSemLocations } from 'clientState/providers/SEM/SemLocationsProvider';
import { useUrlParams } from '../../useUrlParams';
import { usePerformedSemSearchHash } from 'clientState/providers/SEM/SemSearchRequestHashProvider';
import { useDatalayerTracking } from '@edreamsodigeo/frontend-core';
import { Maybe } from 'generated/graphql';

const FLIGHT_ARRIVAL_CITY_CODE = 'flight_arrival_city_code';
const FLIGHT_ARRIVAL_CITY_NAME = 'flight_arrival_city_name';
const FLIGHT_ARRIVAL_COUNTRY_CODE = 'flight_arrival_country_code';
const FLIGHT_ARRIVAL_COUNTRY_NAME = 'flight_arrival_country_name';
const FLIGHT_DEPARTURE_CITY_CODE = 'flight_departure_city_code';
const FLIGHT_DEPARTURE_CITY_NAME = 'flight_departure_city_name';
const FLIGHT_DEPARTURE_COUNTRY_CODE = 'flight_departure_country_code';
const FLIGHT_DEPARTURE_COUNTRY_NAME = 'flight_departure_country_name';
const FLIGHT_CATEGORY = 'flight_category';

type FlightDataLayer = {
  flight_arrival_city_code?: Maybe<string>;
  flight_arrival_city_name?: Maybe<string>;
  flight_arrival_country_code?: Maybe<string>;
  flight_arrival_country_name?: Maybe<string>;
  flight_departure_city_code?: Maybe<string>;
  flight_departure_city_name?: Maybe<string>;
  flight_departure_country_code?: Maybe<string>;
  flight_departure_country_name?: Maybe<string>;
  flight_category?: Maybe<'one way' | 'round trip'>;
};

const flightDataLayerUpdaterParser = ({
  flightCategory,
  flightDepartureCityCode,
  flightDepartureCityName,
  flightDepartureCountryCode,
  flightDepartureCountryName,
  flightArrivalCityCode,
  flightArrivalCityName,
  flightArrivalCountryCode,
  flightArrivalCountryName,
}: {
  flightCategory?: Maybe<'one way' | 'round trip'>;
  flightDepartureCityCode?: Maybe<string>;
  flightDepartureCityName?: Maybe<string>;
  flightDepartureCountryCode?: Maybe<string>;
  flightDepartureCountryName?: Maybe<string>;
  flightArrivalCityCode?: Maybe<string>;
  flightArrivalCityName?: Maybe<string>;
  flightArrivalCountryCode?: Maybe<string>;
  flightArrivalCountryName?: Maybe<string>;
}): FlightDataLayer => {
  return {
    [FLIGHT_ARRIVAL_CITY_CODE]: flightArrivalCityCode,
    [FLIGHT_ARRIVAL_CITY_NAME]: flightArrivalCityName,
    [FLIGHT_ARRIVAL_COUNTRY_CODE]: flightArrivalCountryCode,
    [FLIGHT_ARRIVAL_COUNTRY_NAME]: flightArrivalCountryName,
    [FLIGHT_DEPARTURE_CITY_CODE]: flightDepartureCityCode,
    [FLIGHT_DEPARTURE_CITY_NAME]: flightDepartureCityName,
    [FLIGHT_DEPARTURE_COUNTRY_CODE]: flightDepartureCountryCode,
    [FLIGHT_DEPARTURE_COUNTRY_NAME]: flightDepartureCountryName,
    [FLIGHT_CATEGORY]: flightCategory,
  };
};

export const useFlightDataLayer = (): void => {
  const hash = usePerformedSemSearchHash();
  const { type = 'R', from, to } = useUrlParams(hash);
  const { locations } = useSemLocations();
  const { save: saveDataLayer } = useDatalayerTracking();
  const departure = from ? locations[from] : null;
  const destination = to ? locations[to] : null;
  if (locations && departure && destination) {
    saveDataLayer(
      flightDataLayerUpdaterParser({
        flightCategory: type === 'O' ? 'one way' : 'round trip',
        flightDepartureCityCode: departure.iataCode,
        flightDepartureCityName: departure.cityName,
        flightDepartureCountryCode: departure.countryCode,
        flightDepartureCountryName: departure.countryName,
        flightArrivalCityCode: destination.iataCode,
        flightArrivalCityName: destination.cityName,
        flightArrivalCountryCode: destination.countryCode,
        flightArrivalCountryName: destination.countryName,
      })
    );
  }
};
